import { api } from "@/api/client";
import type { TimeEntry } from "@/api/types";
import { useWorkspaceId } from "@/app/session";
import { invalidateWorkspace } from "@/features/data";
import { useToast, errorMessage } from "@/components/Toast";

/** entryBody rebuilds the write payload for re-creating or duplicating an entry. */
function entryBody(e: TimeEntry) {
  return {
    description: e.description,
    project_id: e.project_id,
    task_id: e.task_id,
    rate_class_id: e.rate_class_id,
    billable: e.billable,
    tags: e.tags.map((t) => t.id),
    start_at: e.start_at,
    end_at: e.end_at,
  };
}

/** useDeleteEntry deletes with a 6s Undo toast that re-creates the entry.
 * Running entries (no end) can't be re-created, so they get no Undo. */
export function useDeleteEntry() {
  const wid = useWorkspaceId();
  const { toast } = useToast();
  return async (entry: TimeEntry) => {
    try {
      await api.del(`/workspaces/${wid}/time-entries/${entry.id}`);
      invalidateWorkspace(wid);
      if (entry.end_at) {
        toast("Entry deleted", "info", {
          duration: 6000,
          action: {
            label: "Undo",
            onClick: () => {
              api
                .post(`/workspaces/${wid}/time-entries`, entryBody(entry))
                .then(() => invalidateWorkspace(wid))
                .catch((err) => toast(errorMessage(err), "error"));
            },
          },
        });
      }
    } catch (err) {
      toast(errorMessage(err), "error");
    }
  };
}

/** useDuplicateEntry creates a copy of an entry with identical fields. */
export function useDuplicateEntry() {
  const wid = useWorkspaceId();
  const { toast } = useToast();
  return async (entry: TimeEntry) => {
    try {
      await api.post(`/workspaces/${wid}/time-entries`, entryBody(entry));
      invalidateWorkspace(wid);
      toast("Entry duplicated", "success");
    } catch (err) {
      toast(errorMessage(err), "error");
    }
  };
}
